/**
 * Export a DOM node to a downloadable A4 PDF.
 * Uses html2canvas to rasterize, then jsPDF to wrap in a PDF document.
 * Image-based PDF — fidelity to the on-screen rendering is the goal.
 */
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

/**
 * @param {HTMLElement} node     DOM node to render
 * @param {string}      filename Output filename (without extension)
 */
export async function downloadNodeAsPdf(node, filename = 'fiche') {
  if (!node) throw new Error('Aucun élément à exporter.');

  // Rasterize at 2× device pixel ratio for crisp text/PDF.
  const canvas = await html2canvas(node, {
    backgroundColor: '#ffffff',
    scale: 2,
    useCORS: true,
    logging: false,
  });
  const imgData = canvas.toDataURL('image/png');

  // A4 portrait: 210 × 297 mm. We center the image with margins.
  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const margin = 12; // mm on each side
  const contentW = pageW - margin * 2;
  // Preserve aspect ratio
  const imgHeight = (canvas.height * contentW) / canvas.width;

  if (imgHeight <= pageH - margin * 2) {
    // Fits a single page
    pdf.addImage(imgData, 'PNG', margin, margin, contentW, imgHeight);
  } else {
    // Multi-page: slice the image vertically across pages
    let remaining = imgHeight;
    let yOffset = 0;
    const pageContentH = pageH - margin * 2;
    while (remaining > 0) {
      pdf.addImage(imgData, 'PNG', margin, margin - yOffset, contentW, imgHeight);
      remaining -= pageContentH;
      yOffset += pageContentH;
      if (remaining > 0) pdf.addPage();
    }
  }

  const safeName = filename
    .replace(/[^\w\u00C0-\u024F-]+/g, '-')   // accents OK
    .replace(/-+/g, '-')
    .toLowerCase();
  pdf.save(`${safeName}.pdf`);
}
